import { css } from '@emotion/react';
import {
  FocusEvent,
  KeyboardEvent,
  ReactElement,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { cx, useSafeHandler } from './helpers';
import { Icon, IconName, IconSize } from './icons';
import { Input, InputProps } from './input';
import { useIcon } from './theme';

export type Option<T> = {
  value: T;
  label: string;
  key: string;
  component?: ReactElement;
};

type FilterOptionFn<T> = (input: string, option: Option<T>) => boolean;

type SelectProps<T> = {
  id: string;
  label: string;
  value: Option<T> | null;
  options: Option<T>[];
  onChange: (value: Option<T>) => void | Promise<void>;
  onInputChange?: (input: string) => void | Promise<void>;
  autoClearInput?: boolean;
  // While the user types, highlight the first match so Enter picks it without an arrow key.
  highlightFirstMatch?: boolean;
  filterOptionsFn?: FilterOptionFn<T>;
  iconName?: IconName;
  className?: string;
  disabled?: boolean;
  autoComplete?: InputProps['autoComplete'];
  subText?: InputProps['subText'];
  subTextFlavor?: InputProps['subTextFlavor'];
  innerRightComponent?: InputProps['innerRightComponent'];
  onBlur?: InputProps['onBlur'];
};

const defaultFilterOptionFn = <T,>(input: string, option: Option<T>) =>
  option.label.toLowerCase().startsWith(input.toLowerCase());

function DropdownIndicator() {
  const icon = useIcon('selectDropdown');
  return <Icon name={icon} size={IconSize.SMALL} color="" />;
}

function SelectOption<T>(props: {
  id: string;
  option: Option<T>;
  onClick: (option: Option<T>) => void;
  selected: boolean;
  active: boolean;
}) {
  const { id, option, onClick, selected, active } = props;
  const wrappedOnClick = useCallback(() => {
    onClick(option);
  }, [option, onClick]);
  const wrappedSafeOnClick = useSafeHandler(wrappedOnClick);

  return (
    <div
      id={id}
      role="option"
      aria-selected={active}
      // Keeps the focus in the input: its blur would close the list before the click lands.
      onMouseDown={e => e.preventDefault()}
      onClick={wrappedSafeOnClick}
      className={cx('aura--select-option', {
        'aura--select-option-selected': selected,
        'aura--select-option-active': active,
      })}
    >
      {option.component || option.label}
    </div>
  );
}

export function Select<T>(props: SelectProps<T>): JSX.Element {
  const {
    id,
    label,
    value,
    options,
    onChange,
    onInputChange,
    autoClearInput = true,
    highlightFirstMatch = false,
    filterOptionsFn = defaultFilterOptionFn,
    iconName,
    className,
    disabled = false,
    autoComplete = 'off',
    subText,
    subTextFlavor,
    innerRightComponent,
    onBlur: onBlurProp,
  } = props;
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listboxRef = useRef<HTMLDivElement>(null);
  const listboxId = `${id}-listbox`;
  const optionId = (index: number) => `${id}-option-${index}`;

  const matchingOptions = options.filter(option =>
    filterOptionsFn(inputValue, option),
  );
  // Keyed, not indexed: options can change under the highlight, e.g. when search results arrive.
  const keyedIndex = matchingOptions.findIndex(
    option => option.key === activeKey,
  );
  const active =
    keyedIndex < 0 &&
    highlightFirstMatch &&
    inputValue &&
    matchingOptions.length
      ? 0
      : keyedIndex;
  const safeOnChange = useSafeHandler(onChange);

  const close = useCallback(() => {
    if (autoClearInput) {
      setInputValue('');
    }
    setActiveKey(null);
    setOpen(false);
  }, [autoClearInput]);

  const onFocus = useCallback(() => {
    setOpen(true);
  }, []);

  const onBlur = useCallback(
    (e: FocusEvent) => {
      onBlurProp?.(e);
      close();
    },
    [close, onBlurProp],
  );

  const onValueChange = useCallback((v: string) => {
    setInputValue(v);
    setActiveKey(null);
    setOpen(true);
  }, []);

  const onClickOption = useCallback(
    (option: Option<T>) => {
      close();
      onChange(option);
      inputRef.current?.blur();
    },
    [onChange, close],
  );

  const onKeyDown = (e: KeyboardEvent) => {
    const count = matchingOptions.length;
    switch (e.key) {
      case 'ArrowDown':
      case 'ArrowUp':
        e.preventDefault();
        if (!open) {
          setOpen(true);
        } else if (count) {
          const next =
            e.key === 'ArrowDown'
              ? (active + 1) % count
              : active <= 0
                ? count - 1
                : active - 1;
          setActiveKey(matchingOptions[next].key);
        }
        break;
      case 'Enter':
        if (open && active >= 0) {
          e.preventDefault();
          close();
          safeOnChange?.(matchingOptions[active]);
        }
        break;
      case 'Escape':
        close();
        break;
    }
  };

  useEffect(() => {
    if (active >= 0) {
      listboxRef.current?.children[active]?.scrollIntoView({
        block: 'nearest',
      });
    }
  }, [active]);

  useEffect(() => {
    onInputChange?.(inputValue);
  }, [onInputChange, inputValue]);

  return (
    <div className={`aura--select ${className || ''}`}>
      <div
        className="aura--select-input"
        // Enter and Escape close the list but keep the focus, so a click on the focused input can't
        // rely on onFocus to reopen it.
        onClick={() => {
          if (document.activeElement === inputRef.current) {
            setOpen(true);
          }
        }}
      >
        <Input
          onInputRef={r => {
            inputRef.current = r;
          }}
          disabled={disabled}
          id={id}
          label={label}
          type="text"
          value={open ? inputValue : value?.label || ''}
          onValueChange={onValueChange}
          innerRightComponent={
            innerRightComponent ||
            (disabled ? undefined : <DropdownIndicator />)
          }
          onFocus={onFocus}
          onBlur={onBlur}
          onKeyDown={onKeyDown}
          autoComplete={autoComplete}
          innerLeftComponent={
            iconName ? (
              <Icon name={iconName} size={IconSize.SMALL} color="#000000" />
            ) : undefined
          }
          subText={subText}
          subTextFlavor={subTextFlavor}
          ariaProps={{
            role: 'combobox',
            'aria-expanded': open,
            'aria-controls': listboxId,
            'aria-autocomplete': 'list',
            'aria-activedescendant': active >= 0 ? optionId(active) : undefined,
          }}
        />
      </div>
      <div
        className="aura--select-options-section"
        css={css({ display: open ? undefined : 'none' })}
      >
        <div className="aura--select-options-container">
          <div
            ref={listboxRef}
            id={listboxId}
            role="listbox"
            aria-label={label}
            className="aura--select-options"
          >
            {matchingOptions.map((option, index) => (
              <SelectOption
                key={option.key || `${option.value}`}
                id={optionId(index)}
                option={option}
                onClick={onClickOption}
                selected={option.value === value?.value}
                active={index === active}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
